import { useState } from 'react';
import { ApiError } from '@/api/client';
import { useCreateDeadline, useDeleteDeadline, useUpdateDeadline } from '@/api/queries';
import type { ChecklistDeadline, EventCard } from '@/api/types';
import { Button } from '@/components/kit/Button';
import { ChoiceField, RowAction } from '@/components/kit/Controls';
import { DatePicker } from '@/components/kit/DatePicker';
import { Form, TextField } from '@/components/kit/Field';
import { Modal } from '@/components/kit/Modal';
import { ActionBar } from '@/components/kit/ActionBar';
import { Alert, EmptyState, Note } from '@/components/kit/Note';
import { SectionHeader } from '@/components/kit/ObjectTabs';
import { Panel, Signals } from '@/components/kit/Panel';
import { Pill } from '@/components/kit/Pill';
import { PopoverSelect } from '@/components/kit/Popover';
import { MobileSub, Na, Table } from '@/components/kit/Table';
import { useToast } from '@/components/kit/Toast';
import { date as fmtDate, parseRuDate, plural } from '@/lib/format';
import { CHECKLIST_DEADLINE_ITEMS } from '../state';

/**
 * Вкладка «Дедлайны».
 *
 * Дата позже начала мероприятия **сохраняется и подсвечивается** —
 * это предупреждение, а не отказ (БЛ §9). Публикацию такой дедлайн
 * при этом не пропустит: проверка 11 из двенадцати.
 */
export function DeadlinesTab({ event }: { event: EventCard }) {
  const update = useUpdateDeadline(event.id);
  const remove = useDeleteDeadline(event.id);
  const toast = useToast();
  const [adding, setAdding] = useState(false);

  const late = event.deadlines.filter((d) => d.isLate);

  const fail = (e: Error) => toast.showError(e instanceof ApiError ? e.message : 'Не получилось');

  const addButton = (
    <Button small tone="quiet" onClick={() => setAdding(true)}>
      ＋ Дедлайн
    </Button>
  );

  return (
    <>
      <Signals>
        {late.length ? (
          <Alert>
            <b>
              {late.length} {plural(late.length, 'дедлайн', 'дедлайна', 'дедлайнов')} позже начала
              мероприятия.
            </b>
            <span>Такой срок не успеет сработать — опубликовать мероприятие он не даст</span>
          </Alert>
        ) : null}
      </Signals>

      <Panel>
        <SectionHeader actions={addButton} />

        {!event.deadlines.length ? (
          <EmptyState>
            Дедлайнов пока нет. Дедлайн — срок, после которого что-то перестаёт приниматься
          </EmptyState>
        ) : (
          <Table
            valueWidth={130}
            head={
              <tr>
                <th>Что</th>
                <th>Дата</th>
                <th>Что блокирует</th>
                <th>Пункт чек-листа</th>
                <th />
              </tr>
            }
          >
            {event.deadlines.map((d) => (
              <tr key={d.id}>
                <td data-col="primary">
                  <span className="nm">{d.name}</span>
                  <MobileSub>{d.blocksWhat ?? '—'}</MobileSub>
                </td>
                <td data-col="secondary">
                  <DatePicker
                    value={d.date}
                    onChange={(date) =>
                      update.mutate({ id: d.id, date }, { onError: fail })
                    }
                  />
                  {d.isLate ? <Pill tone="warn">позже начала</Pill> : null}
                </td>
                <td className="muted ell" data-col="hide">
                  {d.blocksWhat ?? <Na />}
                </td>
                {/* Срок пункта чек-листа номеров (БЛ M3 5.21) — правится прямо в строке */}
                <td data-col="hide">
                  <PopoverSelect
                    value={d.checklistItem ?? 'none'}
                    options={CHECKLIST_DEADLINE_ITEMS}
                    onChange={(v) =>
                      update.mutate({ id: d.id, checklistItem: v === 'none' ? null : v }, { onError: fail })
                    }
                  />
                </td>
                <td className="r" data-col="act">
                  <RowAction
                    title="Удалить дедлайн"
                    onClick={() =>
                      remove.mutate(d.id, {
                        onSuccess: () => toast.show(`«${d.name}» удалён`),
                        onError: fail,
                      })
                    }
                  />
                </td>
              </tr>
            ))}
          </Table>
        )}

        <Note>
          При копировании мероприятия дедлайны сдвигаются вместе с датой события: если срок стоял
          за 17 дней до старта, он останется за 17 дней
        </Note>
      </Panel>

      <ActionBar>{addButton}</ActionBar>

      {adding ? <NewDeadlineModal event={event} onClose={() => setAdding(false)} /> : null}
    </>
  );
}

function NewDeadlineModal({ event, onClose }: { event: EventCard; onClose: () => void }) {
  const create = useCreateDeadline(event.id);
  const toast = useToast();

  const [name, setName] = useState('');
  // Дата набирается руками: календарь это поповер, а поповер внутри
  // окна обрезается его `overflow:hidden` (ТЗ §7). В списке дедлайнов,
  // где окна нет, дата правится календарём — см. таблицу выше.
  const [dateText, setDateText] = useState(fmtDate(event.dateFrom));
  const [blocksWhat, setBlocksWhat] = useState('');
  const [checklist, setChecklist] = useState<ChecklistDeadline | 'none'>('none');

  const date = parseRuDate(dateText);
  const touched = dateText.trim().length > 0;
  const late = date ? date > event.dateFrom : false;

  const submit = () =>
    create.mutate(
      { name, date, blocksWhat: blocksWhat || null, checklistItem: checklist === 'none' ? null : checklist },
      {
        onSuccess: () => {
          toast.show(`Дедлайн «${name}» добавлен`);
          onClose();
        },
        onError: (e) => toast.showError(e instanceof ApiError ? e.message : 'Не добавилось'),
      },
    );

  return (
    <Modal
      title="Новый дедлайн"
      onClose={onClose}
      confirm={{
        label: 'Добавить',
        onClick: submit,
        disabled: !name.trim() || !date || create.isPending,
      }}
    >
      <Form>
        <TextField label="Что" value={name} onValue={setName} placeholder="Загрузка музыки" />
        <TextField
          label="Дата"
          value={dateText}
          onValue={setDateText}
          placeholder="ДД.ММ.ГГГГ"
          size={14}
          error={touched && !date ? 'Дата в формате ДД.ММ.ГГГГ' : null}
        />
        <TextField
          label="Что блокирует"
          value={blocksWhat}
          onValue={setBlocksWhat}
          placeholder="допуск номера"
        />
        {/* В окне — выбор из вариантов: поповер внутри .modal обрезается (CLAUDE.md) */}
        <ChoiceField
          label="Закрывает пункт чек-листа номеров"
          items={CHECKLIST_DEADLINE_ITEMS}
          value={checklist}
          onChange={setChecklist}
        />
      </Form>

      {late ? (
        <Note tone="warn">
          Этот срок позже начала мероприятия. Система его примет и подсветит, но опубликовать
          мероприятие с ним не даст
        </Note>
      ) : (
        <Note>
          Дату позже начала мероприятия система примет и подсветит — но опубликовать
          мероприятие с таким дедлайном не даст
        </Note>
      )}
    </Modal>
  );
}
